import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import dayjs from "dayjs";
import React from "react";
import { RouteComponentProps } from "react-router";

import {
  PremierCollectionBenefits,
  ProductImageModal,
  useDeviceTypes,
  GENERIC_HOTEL_PROMOTION_OFFER_IMAGE,
} from "halifax";
import {
  AUTOCOMPLETE_LOCATION_SELECTED,
  AutocompleteLocationSelectedProperties,
  CALENDAR_DATES_SELECTED,
  CalendarDatesSelectedProperties,
  IResult,
  LAUNCHED_MOBILE_LOCATION_SCREEN,
  LaunchedMobileLocationScreenProperties,
  MODAL_ALERT,
  ModalAlertProperties,
  ModalCategoryType,
  ModalScreens,
  StayTypesEnum,
  VIEWED_PREMIUM_OFFER_PAGE,
  ViewedCompNightBannerEntryPoint,
  ViewedPremiumOfferPageProperties,
} from "redmond";

import {
  COMPLIMENTARY_NIGHT_PROMOTION_BUTTON_TEXT,
  COMPLIMENTARY_NIGHT_PROMOTION_DESCRIPTION,
  COMPLIMENTARY_NIGHT_PROMOTION_TITLE,
  HAS_SEEN_COMPLIMENTARY_NIGHT_PROMOTION_MODAL_KEY,
  PATH_PREMIUM_OFFERS_SHOP,
  PATH_TERMS_COMPLIMENTARY_NIGHT,
} from "@capone/common";
import {
  getDefaultExperimentVariant,
  LUXURY_COMPLIMENTARY_NIGHT_PROMOTION_ENTRYPOINTS,
} from "@capone/experiments";
import { useDispatch } from "react-redux";
import { trackEvent } from "../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  getExperimentVariant,
  LC_GATING_EXPERIMENT,
  LUXURY_MEGA_MENU_EXPERIMENT,
  PREMIER_COLLECTION_EXPERIMENT,
  useExperiments,
  VR_FOR_NON_PREMIUM_CARDHOLDERS_EXPERIMENT,
  VR_FOR_PREMIUM_CARDHOLDERS_EXPERIMENT,
} from "../../context/experiments";
import { PATH_HOME, PATH_VACATION_RENTALS } from "../../utils/paths";

import { MobilePremierCollectionSearchControlV3 } from "./components/MobilePremierCollectionSearchControlV3";
import { PremierCollectionSearchControl } from "./components/PremierCollectionSearchControl";
import { PremierCollectionSearchConnectorProps } from "./container";
import * as textConstants from "./textConstants";

import {
  fetchPCLCLocationCategories,
  setMegaMenuEnabled,
} from "./actions/actions";
import "./styles.scss";

export interface IPremierCollectionSearchProps
  extends PremierCollectionSearchConnectorProps,
    RouteComponentProps {}

export const PremierCollectionSearch = (
  props: IPremierCollectionSearchProps
) => {
  const {
    fetchMegaMenuRegions,
    fetchRewardsAccounts,
    fetchLocationCategories,
    largestValueAccount,
    rewardsAccounts,
    fetchLifestyleLocationCategories,
    stayType,
    fetchVacationRentalsLocationCategories,
    vacationRentalsLocationCategories,
    setStayType,
    history,
    listPaymentMethods,
    isVentureX,
  } = props;
  const { matchesMobile } = useDeviceTypes();

  const expState = useExperiments();

  const isPremierCollectionEnabled =
    getExperimentVariant(
      expState.experiments,
      PREMIER_COLLECTION_EXPERIMENT
    ) === AVAILABLE;

  const isLifestyleOnlyCollectionEnabled =
    getExperimentVariant(expState.experiments, LC_GATING_EXPERIMENT) ===
      AVAILABLE && !isPremierCollectionEnabled;

  const VRForPremiumCardHolders = getExperimentVariant(
    expState.experiments,
    VR_FOR_PREMIUM_CARDHOLDERS_EXPERIMENT
  );
  const isVRForPremiumCardHoldersEnabled = React.useMemo(
    () => VRForPremiumCardHolders === AVAILABLE,
    [VRForPremiumCardHolders]
  );

  const VRForNonPremiumCardHolders = getExperimentVariant(
    expState.experiments,
    VR_FOR_NON_PREMIUM_CARDHOLDERS_EXPERIMENT
  );
  const isVRForNonPremiumCardHoldersEnabled = React.useMemo(
    () => VRForNonPremiumCardHolders === AVAILABLE,
    [VRForNonPremiumCardHolders]
  );

  const LuxuryMegaMenuExperiment = getExperimentVariant(
    expState.experiments,
    LUXURY_MEGA_MENU_EXPERIMENT
  );
  const enableMegaMenu = React.useMemo(
    () => LuxuryMegaMenuExperiment === AVAILABLE,
    [LuxuryMegaMenuExperiment]
  );

  const complimentaryNightPromotionEntrypointsExperiment =
    getDefaultExperimentVariant(
      expState.experiments,
      LUXURY_COMPLIMENTARY_NIGHT_PROMOTION_ENTRYPOINTS
    );
  const isComplimentaryNightPromotionEntrypointsEnabledExperiment =
    React.useMemo(
      () => complimentaryNightPromotionEntrypointsExperiment === AVAILABLE,
      [complimentaryNightPromotionEntrypointsExperiment]
    );

  const hasSeenComplimentaryNightPromotionModal = localStorage.getItem(
    HAS_SEEN_COMPLIMENTARY_NIGHT_PROMOTION_MODAL_KEY
  );

  const [
    openComplimentaryNightPromotionModal,
    setOpenComplimentaryNightPromotionModal,
  ] = React.useState(false);

  React.useEffect(() => {
    if (
      !hasSeenComplimentaryNightPromotionModal &&
      isComplimentaryNightPromotionEntrypointsEnabledExperiment &&
      stayType === StayTypesEnum.Hotels
    ) {
      const properties: ModalAlertProperties = {
        type: "luxury_complimentary_night_promotion",
        screen: ModalScreens.PREMIUM_STAYS_LANDING,
        primary_button: COMPLIMENTARY_NIGHT_PROMOTION_BUTTON_TEXT,
        secondary_button: "",
        step: "search",
        funnel: "premier_collection",
        category: ModalCategoryType.FEATURE,
        modal_subtitle: COMPLIMENTARY_NIGHT_PROMOTION_DESCRIPTION(isVentureX),
        modal_title: COMPLIMENTARY_NIGHT_PROMOTION_TITLE,
        agent_title: "",
        agent_subtitle: "",
        lob: "hotel",
      };
      trackEvent({ eventName: MODAL_ALERT, properties });
      localStorage.setItem(
        HAS_SEEN_COMPLIMENTARY_NIGHT_PROMOTION_MODAL_KEY,
        "true"
      );
      setOpenComplimentaryNightPromotionModal(true);
    }
  }, [isComplimentaryNightPromotionEntrypointsEnabledExperiment, stayType]);

  React.useEffect(() => {
    fetchRewardsAccounts();
    listPaymentMethods();
    isLifestyleOnlyCollectionEnabled
      ? fetchLifestyleLocationCategories()
      : fetchLocationCategories();
    setTimeout(() => window.scrollTo(0, 0), 0);
  }, []);

  React.useEffect(() => {
    if (expState.experiments.length > 0) {
      if (
        history?.location.pathname === PATH_VACATION_RENTALS &&
        !isVRForPremiumCardHoldersEnabled &&
        !isVRForNonPremiumCardHoldersEnabled
      ) {
        history.push(PATH_HOME);
      }

      if (
        history?.location.pathname === PATH_VACATION_RENTALS &&
        (isVRForPremiumCardHoldersEnabled ||
          isVRForNonPremiumCardHoldersEnabled)
      ) {
        setStayType(StayTypesEnum.VacationRentals);
      } else {
        setStayType(StayTypesEnum.Hotels);
      }
    }
  }, [expState, history]);

  React.useEffect(() => {
    if (
      vacationRentalsLocationCategories.length === 0 &&
      stayType === StayTypesEnum.VacationRentals &&
      (isVRForPremiumCardHoldersEnabled || isVRForNonPremiumCardHoldersEnabled)
    ) {
      fetchVacationRentalsLocationCategories();
    }
  }, [stayType]);

  React.useEffect(() => {
    if (matchesMobile) {
      const properties: LaunchedMobileLocationScreenProperties = {
        funnel: "premier_collection",
        url: window.location.pathname,
      };
      trackEvent({ eventName: LAUNCHED_MOBILE_LOCATION_SCREEN, properties });
    }
  }, [matchesMobile]);

  const dispatch = useDispatch();
  React.useEffect(() => {
    if (enableMegaMenu) {
      fetchMegaMenuRegions();
      dispatch(fetchPCLCLocationCategories());
      dispatch(setMegaMenuEnabled(true));
    }
  }, [enableMegaMenu]);

  const handleSelectLocation = React.useCallback((value: IResult | null) => {
    if (value?.label) {
      const properties: AutocompleteLocationSelectedProperties = {
        funnel: "premier_collection",
        url: window.location.pathname,
        autocomplete_value: value.label,
      };
      trackEvent({ eventName: AUTOCOMPLETE_LOCATION_SELECTED, properties });
    }
  }, []);

  const handleSelectDates = React.useCallback(
    (fromDate: Date | null, untilDate: Date | null) => {
      if (fromDate && untilDate) {
        const properties: CalendarDatesSelectedProperties = {
          funnel: "premier_collection",
          url: window.location.pathname,
          start_date: dayjs(fromDate).format("YYYY-MM-DD"),
          end_date: dayjs(untilDate).format("YYYY-MM-DD"),
        };
        trackEvent({ eventName: CALENDAR_DATES_SELECTED, properties });
      }
    },
    []
  );

  if (matchesMobile) {
    return (
      <Box className="mobile-premier-collection-search-root">
        <Box className="premier-collection-search-container-mobile">
          <MobilePremierCollectionSearchControlV3
            supportsMegaMenu={enableMegaMenu}
            onSelectLocation={handleSelectLocation}
            onSelectDates={handleSelectDates}
            isLifestyleCollection={isLifestyleOnlyCollectionEnabled}
            includesLifestyleCollection={isPremierCollectionEnabled}
            includesVacationRentals={
              isVRForPremiumCardHoldersEnabled ||
              isVRForNonPremiumCardHoldersEnabled
            }
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      className={clsx("premier-collection-search-root", {
        "premium-stays": isPremierCollectionEnabled,
        "lifestyle-collection": isLifestyleOnlyCollectionEnabled,
        "with-vacation-rentals":
          isVRForPremiumCardHoldersEnabled ||
          isVRForNonPremiumCardHoldersEnabled,
        "premium-vacation-rentals":
          stayType === StayTypesEnum.VacationRentals &&
          (isVRForPremiumCardHoldersEnabled ||
            isVRForNonPremiumCardHoldersEnabled),
        "non-premium-vacation-rentals":
          stayType === StayTypesEnum.VacationRentals &&
          isVRForNonPremiumCardHoldersEnabled,
      })}
    >
      <ProductImageModal
        imageUrl={GENERIC_HOTEL_PROMOTION_OFFER_IMAGE}
        title={COMPLIMENTARY_NIGHT_PROMOTION_TITLE}
        description={COMPLIMENTARY_NIGHT_PROMOTION_DESCRIPTION(isVentureX)}
        buttonText={COMPLIMENTARY_NIGHT_PROMOTION_BUTTON_TEXT}
        buttonAction={() => {
          history.push(PATH_PREMIUM_OFFERS_SHOP);
        }}
        termsAndConditionsUrl={PATH_TERMS_COMPLIMENTARY_NIGHT}
        openModal={openComplimentaryNightPromotionModal}
        setOpenModal={setOpenComplimentaryNightPromotionModal}
      />
      <Box className="premier-collection-search-top-section">
        <Box className="premier-collection-search-container-desktop">
          <Typography variant="h2" className="search-title">
            {((isVRForPremiumCardHoldersEnabled ||
              isVRForNonPremiumCardHoldersEnabled) &&
              largestValueAccount &&
              textConstants.TITLE_WITH_EARN(
                stayType === StayTypesEnum.VacationRentals
                  ? 5
                  : largestValueAccount?.earn.hotelsMultiplier,
                largestValueAccount?.productDisplayName,
                stayType === StayTypesEnum.VacationRentals
              )) ||
              (isLifestyleOnlyCollectionEnabled &&
                textConstants.LC_FOR_NON_PREMIUM_CARDHOLDERS_TITLE) ||
              (isPremierCollectionEnabled &&
                textConstants.LC_FOR_PREMIUM_CARDHOLDERS_TITLE) ||
              textConstants.TITLE_FIRST_HALF}
          </Typography>
          <Typography variant="h2" className="search-subtitle">
            {(isVRForNonPremiumCardHoldersEnabled &&
              stayType === StayTypesEnum.VacationRentals &&
              textConstants.VR_SUBTITLE) ||
              (isLifestyleOnlyCollectionEnabled &&
                textConstants.LC_FOR_NON_PREMIUM_CARDHOLDERS_SUBTITLE) ||
              (isPremierCollectionEnabled &&
                textConstants.LC_FOR_PREMIUM_CARDHOLDERS_SUBTITLE) ||
              textConstants.SUBTITLE}
          </Typography>

          <PremierCollectionSearchControl
            supportsMegaMenu={enableMegaMenu}
            includesLifestyleCollection={
              isPremierCollectionEnabled || isLifestyleOnlyCollectionEnabled
            }
            isLifestyleCollection={isLifestyleOnlyCollectionEnabled}
            includesVacationRentals={
              isVRForPremiumCardHoldersEnabled ||
              isVRForNonPremiumCardHoldersEnabled
            }
          />
        </Box>
      </Box>
      <PremierCollectionBenefits
        largestValueAccount={largestValueAccount}
        variant={(() => {
          if (isPremierCollectionEnabled) return "with-lifestyle-collection";
          if (isLifestyleOnlyCollectionEnabled)
            return "only-lifestyle-collection";
          return "default";
        })()}
        rewardsAccounts={rewardsAccounts}
        includesVacationRentals={
          isVRForPremiumCardHoldersEnabled ||
          isVRForNonPremiumCardHoldersEnabled
        }
        stayType={stayType}
        isComplimentaryNightPromotionEntrypointsEnabledExperiment={
          isComplimentaryNightPromotionEntrypointsEnabledExperiment
        }
        onShowComplimentaryNightPromotion={() => {
          trackEvent({
            eventName: VIEWED_PREMIUM_OFFER_PAGE,
            properties: {
              entry_point:
                ViewedCompNightBannerEntryPoint.PREMIUM_STAYS_LANDING,
            } as ViewedPremiumOfferPageProperties,
          });
          history.push(PATH_PREMIUM_OFFERS_SHOP);
        }}
        trackEvent={trackEvent}
        entryPoint={ViewedCompNightBannerEntryPoint.PREMIUM_STAYS_LANDING}
      />
    </Box>
  );
};
